import {
    AbstractMesh,
    PBRMaterial,
    Quaternion,
    SceneLoader,
    TransformNode,
    Vector3,
    type AssetContainer,
    type Node,
    type Scene,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { INTERACTABLE_OUTLINE_COLOR, MeshOutlines, type MeshOutlineLayer } from './vfx/mesh-outlines';
import { SceneLoadOnceCaches } from './load-once-cache';
import { resolveSceneYaw } from './world-to-scene-position';

/** How the chest should look this frame; all fields are derived from replicated state. */
export interface TreasureChestAppearance {
    /** Compass degrees its front faces: 0 north (-y tiles), 90 east (+x tiles). */
    readonly yawDegrees: number;
    readonly isOpened: boolean;
    /** Under the cursor, or what the player is walking over to open. */
    readonly isHighlighted: boolean;
    /** 1 drawn in full, down to 0 as an opened chest fades away. */
    readonly opacity: number;
}

// The chest model per scene and file, loaded once: chests come into and out
// of view as the player explores.
const containers = new SceneLoadOnceCaches<string, AssetContainer>((container) => container.dispose());

function loadContainer(scene: Scene, rootUrl: string, fileName: string): Promise<AssetContainer> {
    return containers.of(scene).get(`${rootUrl}${fileName}`, () => SceneLoader.LoadAssetContainerAsync(rootUrl, fileName, scene));
}

/** The lid's node in the chest models (Quaternius' chest and gold chest). */
const LID_NODE_NAME = 'Chest_Top';
/**
 * The lid's rotation shut: the closed chest model's (`chest.glb`) own. The
 * gold chest this model is drawn from has its lid authored open, which is
 * where the lid swings to; both share the hinge at the lid's back edge.
 */
const CLOSED_LID_ROTATION = new Quaternion(-0.7071068, 0, 0, 0.7071067);
/** How tall the chest stands with its lid shut, in tiles: a little larger than the rooms' scenery chests, so it reads as the prize. */
const CHEST_HEIGHT_TILES = 0.6;

/** Seconds the lid takes to swing open. */
const LID_SWING_SECONDS = 0.5;

/**
 * A treasure room's chest (#271): the gold chest model, its lid shut until
 * the chest is opened. Opening it while it is in view swings the lid back on
 * its hinge, easing to a stop; a chest first seen already open just shows
 * open. It fades as it goes (`opacity`). Loaded from the dungeon props
 * (game-content/assets/props/dungeon), re-centered so its base sits on the
 * floor at its origin, and sized to `CHEST_HEIGHT_TILES`.
 */
export class TreasureChestModel {
    private readonly root: TransformNode;
    private readonly lid: TransformNode;
    private readonly openLidRotation: Quaternion;
    private readonly meshes: readonly AbstractMesh[];
    private outline: MeshOutlineLayer | null = null;
    private isHighlighted = false;
    /** Null until the first appearance; then whether it shows (or is opening) open. */
    private isOpened: boolean | null = null;
    /** Seconds since it began opening in view; null when not opening. */
    private openingElapsedSeconds: number | null = null;

    private constructor(root: TransformNode, lid: TransformNode, openLidRotation: Quaternion, meshes: readonly AbstractMesh[]) {
        this.root = root;
        this.lid = lid;
        this.openLidRotation = openLidRotation;
        this.meshes = meshes;
    }

    /** Loads the chest GLB ahead of the first `load`, so showing a chest later loads nothing. */
    public static async preload(scene: Scene, rootUrl: string, fileName: string): Promise<void> {
        await loadContainer(scene, rootUrl, fileName);
    }

    /**
     * Adds a chest to `scene` from the chest GLB, loading it the first time.
     * `rootUrl` + `fileName` follow `SceneLoader.LoadAssetContainerAsync`.
     */
    public static async load(scene: Scene, rootUrl: string, fileName: string): Promise<TreasureChestModel> {
        const container = await loadContainer(scene, rootUrl, fileName);
        // Materials are cloned: each chest fades on its own (`prepareToFade`).
        const instance = container.instantiateModelsToScene((name) => name, true);
        const modelRoot = instance.rootNodes[0];
        if (!(modelRoot instanceof TransformNode)) {
            throw new Error(`TreasureChestModel: ${rootUrl}${fileName} imported zero nodes.`);
        }
        const lid = modelRoot.getDescendants(false, (node: Node) => node.name === LID_NODE_NAME)[0];
        if (!(lid instanceof TransformNode) || !lid.rotationQuaternion) {
            throw new Error(`TreasureChestModel: ${rootUrl}${fileName} has no ${LID_NODE_NAME} lid node.`);
        }
        const openLidRotation = lid.rotationQuaternion.clone();

        // Measured shut, so the open lid doesn't count toward its size.
        lid.rotationQuaternion.copyFrom(CLOSED_LID_ROTATION);
        modelRoot.computeWorldMatrix(true);
        const { min, max } = modelRoot.getHierarchyBoundingVectors(true);
        const center = Vector3.Center(min, max);
        const scale = CHEST_HEIGHT_TILES / Math.max(0.001, max.y - min.y);

        // Center horizontally and put the model's lowest point on the root's origin.
        const root = new TransformNode('treasure-chest', scene);
        root.scaling.setAll(scale);
        modelRoot.parent = root;
        modelRoot.position.set(-center.x, -min.y, -center.z);
        const meshes = modelRoot.getChildMeshes(false);
        meshes.forEach(prepareToFade);
        return new TreasureChestModel(root, lid, openLidRotation, meshes);
    }

    /** Every mesh of the model, for pick filtering. */
    public getMeshes(): readonly AbstractMesh[] {
        return this.meshes;
    }

    /** Places the chest's base at a scene position. */
    public setPosition(position: { readonly x: number; readonly y: number; readonly z: number }): void {
        this.root.position.set(position.x, position.y, position.z);
    }

    public setAppearance(appearance: TreasureChestAppearance): void {
        // The model's front is its local +Z, as the map's props are.
        this.root.rotation.y = resolveSceneYaw(appearance.yawDegrees);
        this.setOpened(appearance.isOpened);
        this.setHighlighted(appearance.isHighlighted && !appearance.isOpened);
        this.setOpacity(appearance.opacity);
    }

    public isDisposed(): boolean {
        return this.root.isDisposed();
    }

    /** Advances the opening, if it is opening. */
    public update(deltaSeconds: number): void {
        if (this.openingElapsedSeconds === null || this.root.isDisposed()) {
            return;
        }

        this.openingElapsedSeconds += deltaSeconds;
        const swing = Math.min(1, this.openingElapsedSeconds / LID_SWING_SECONDS);
        // Eases out: lifted quickly, settling open.
        Quaternion.SlerpToRef(CLOSED_LID_ROTATION, this.openLidRotation, 1 - (1 - swing) ** 3, this.lid.rotationQuaternion!);

        if (swing >= 1) {
            this.openingElapsedSeconds = null;
            this.lid.rotationQuaternion!.copyFrom(this.openLidRotation);
        }
    }

    public dispose(): void {
        this.outline?.dispose();
        // This chest's materials are its own clones, but their textures are
        // the cached model's, which the next chest shown still uses.
        for (const mesh of this.meshes) {
            mesh.material?.dispose(false, false);
        }
        this.root.dispose(false, false);
    }

    /** The first appearance shows it as it is; a shut chest opening after that opens in view. */
    private setOpened(isNowOpened: boolean): void {
        if (this.isOpened === isNowOpened) {
            return;
        }
        const isFirstSeen = this.isOpened === null;
        this.isOpened = isNowOpened;
        if (!isNowOpened) {
            this.openingElapsedSeconds = null;
            this.lid.rotationQuaternion!.copyFrom(CLOSED_LID_ROTATION);
            return;
        }
        if (isFirstSeen) {
            this.lid.rotationQuaternion!.copyFrom(this.openLidRotation);
            return;
        }
        this.openingElapsedSeconds = 0;
    }

    /** Fades it (see `prepareToFade`). */
    private setOpacity(opacity: number): void {
        for (const mesh of this.meshes) {
            mesh.visibility = opacity;
        }
    }

    private setHighlighted(isNowHighlighted: boolean): void {
        if (this.isHighlighted === isNowHighlighted) {
            return;
        }
        this.isHighlighted = isNowHighlighted;
        if (isNowHighlighted) {
            this.outline ??= MeshOutlines.of(this.root.getScene()).layer('target');
            this.outline.set(this.meshes, INTERACTABLE_OUTLINE_COLOR);
        } else {
            this.outline?.clear();
        }
    }
}

/**
 * Sets a mesh's material up to fade, once, when the chest loads. A glTF
 * material is marked opaque (`transparencyMode`), which for a PBR material
 * wins over the mesh's visibility: it was drawn unblended, writing its faded
 * alpha straight into the canvas, so the page behind showed through. Switching
 * it to blend only when the fade began recompiled its shaders, and the chest
 * went undrawn until they were ready. So it always blends: whole, it draws
 * as before. A depth pre-pass keeps a fading chest solid (its far side and
 * insides never show through its near side), and the reflections and
 * highlights fade with its color rather than staying whole over it
 * (`useRadianceOverAlpha`, `useSpecularOverAlpha`), which washed it out white.
 */
function prepareToFade(mesh: AbstractMesh): void {
    const material = mesh.material;
    if (material instanceof PBRMaterial) {
        material.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHABLEND;
        material.needDepthPrePass = true;
        material.useRadianceOverAlpha = false;
        material.useSpecularOverAlpha = false;
    }
}
